import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton
} from '@mui/material';
import { db } from '../firebase';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import ViewListIcon from '@mui/icons-material/ViewList';
import TodayIcon from '@mui/icons-material/Today';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BottomNav from './BottomNav';
import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { format, isSameDay } from 'date-fns';

interface Appointment {
  id: string;
  customerName: string;
  customerPhone: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  notes?: string;
  service?: string;
  customerId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointment-tabpanel-${index}`}
      aria-labelledby={`appointment-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `appointment-tab-${index}`,
    'aria-controls': `appointment-tabpanel-${index}`,
  };
}

const AppointmentsView: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, 'appointments'), orderBy('appointmentDate', 'desc'));
        const querySnapshot = await getDocs(q);

        const appointmentsData: Appointment[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data() as Omit<Appointment, 'id'>
        }));

        setAppointments(appointmentsData);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const filteredAppointments = appointments.filter(appointment => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (appointment.customerName || '').toLowerCase().includes(searchTermLower) ||
      (appointment.service || '').toLowerCase().includes(searchTermLower) ||
      (appointment.customerPhone || '').includes(searchTerm) ||
      (appointment.notes || '').toLowerCase().includes(searchTermLower)
    );
  });

  const todayAppointments = appointments.filter(appointment => {
    const today = new Date();
    const appDate = new Date(appointment.appointmentDate);
    return isSameDay(today, appDate);
  });

  const formatAppointmentDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const renderStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; gradient: string }> = {
      completed: {
        label: 'Completed',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
      },
      canceled: {
        label: 'Cancelled',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      },
      cancelled: {
        label: 'Cancelled',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      },
      default: {
        label: 'Scheduled',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.default;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          background: config.gradient,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26,
        }}
      />
    );
  };

  const renderAppointmentCard = (appointment: Appointment) => (
    <Card
      key={appointment.id}
      sx={{
        mb: 2,
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: 44,
                height: 44,
                mr: 2
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                {appointment.customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {appointment.customerPhone}
              </Typography>
            </Box>
          </Box>
          {renderStatusChip(appointment.status)}
        </Box>

        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarMonthIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main, mr: 0.75 }}
            />
            <Typography variant="body2" fontWeight={500}>
              {formatAppointmentDate(appointment.appointmentDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main, mr: 0.75 }}
            />
            <Typography variant="body2" fontWeight={500}>
              {appointment.appointmentTime}
            </Typography>
          </Box>
        </Box>

        {appointment.service && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              mb: appointment.notes ? 1.5 : 0
            }}
          >
            <Typography variant="body2" fontWeight={600} color="primary">
              {appointment.service}
            </Typography>
          </Box>
        )}

        {appointment.notes && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.grey[500], 0.05),
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
              Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appointment.notes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 10 }}>
      {/* Modern Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0 0 32px 32px',
          pt: 3,
          pb: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                Appointments
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {appointments.length} total • {todayAppointments.length} today
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ mt: -2, position: 'relative', zIndex: 2 }}>
        {/* Search Box */}
        <Card
          sx={{
            mb: 3,
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name, service, phone..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    border: 'none',
                  },
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card
          sx={{
            mb: 3,
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                py: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
              },
              '& .Mui-selected': {
                color: theme.palette.primary.main,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              icon={<ViewListIcon />}
              iconPosition="start"
              label="All"
              {...a11yProps(0)}
            />
            <Tab
              icon={<TodayIcon />}
              iconPosition="start"
              label={`Today (${todayAppointments.length})`}
              {...a11yProps(1)}
            />
          </Tabs>
        </Card>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Card key={i} sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', mb: 2 }}>
                      <Skeleton variant="circular" width={44} height={44} sx={{ mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '12px' }} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : filteredAppointments.length > 0 ? (
            <Box>
              {filteredAppointments.map(renderAppointmentCard)}
            </Box>
          ) : (
            <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3,
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    }}
                  >
                    <EventNoteIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    No appointments found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'Try adjusting your search' : 'No appointments available'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box>
              {[1, 2].map((i) => (
                <Card key={i} sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', mb: 2 }}>
                      <Skeleton variant="circular" width={44} height={44} sx={{ mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '12px' }} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : todayAppointments.length > 0 ? (
            <Box>
              {todayAppointments.map(renderAppointmentCard)}
            </Box>
          ) : (
            <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3,
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    }}
                  >
                    <TodayIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    No appointments today
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enjoy your day off!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default AppointmentsView;
